import globalMessage from "@/utils/globalMessage";
import { Alert, Breadcrumb, Button, Checkbox, ConfigProvider, Divider, Drawer, Form, Input, Menu, Modal, Segmented, Space, Spin, Switch, Tag, Tooltip, Tree, Upload, message } from "antd";
import Table from "@components/ResponsiveTable";
import ResponsiveModal from "@components/ResponsiveModal";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FileIcon, FolderIcon } from './Icon';
import { AppstoreOutlined, BarsOutlined, ClockCircleOutlined, CloseOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, ExclamationCircleOutlined, EyeInvisibleOutlined, EyeOutlined, FolderAddOutlined, FolderFilled, FundViewOutlined, HighlightOutlined, HomeOutlined, MoreOutlined, ReloadOutlined, ScissorOutlined, SearchOutlined, SortAscendingOutlined, StarFilled, StarOutlined, UploadOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import { FileManagementWrap, ActionBarWrap } from './style';
import _ from 'lodash';
import { api } from '@/redux/api';

import { jsforceResponse } from '@/config/httpService';
import { allowedFileTypes, beforeUpload, copyLink, FILE_MANAGEMENT_DELETE_CONFIRM_MODAL_Z_INDEX, FILE_MANAGEMENT_POPUP_Z_INDEX, formatFilesToTreeData, getFileExtension, getPathsByCurrentTree, getSafeUrl, openInApp, openInBrowser, tablelocale, usePreviewUrl } from './utils';
import { useClickAway, useCounter, useSize } from 'ahooks';
import { FileViewer } from './doc-viewer';
import './index.css'
import moment from 'moment';
import { FormItemTag } from './FormItemTag';
import { CategoryOptions, CategoryTag } from './categoryTag';
import { DropdownMenu } from './DropdownMenu';
import { MoveToModal } from './MoveToModal';
import { useSelector } from 'react-redux';
import { CardTable } from './CardTable';
import ContextMenu from './ContextMenu';
import { useAuth, AUTH_LEVEL } from '@/hooks/use-auth';
import TagPill from '@components/TagPill';
import Select from "@components/ResponsiveSelect";
import TreeSelect from "@components/ResponsiveTreeSelect";

const checkFileType = (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  if (allowedFileTypes.includes(extension)) {
    return true;
  }
  return false;
};

const formatFileSize = (bytes) => {
  if (bytes == null || isNaN(bytes)) return '-';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1) + ' ' + units[i];
};

const fileTypeOptions = [
  { value: 'pdf', label: 'PDF' },
  { value: 'doc', label: 'Word' },
  { value: 'docx', label: 'Word' },
  { value: 'xls', label: 'Excel' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'ppt', label: 'PowerPoint' },
  { value: 'pptx', label: 'PowerPoint' },
  { value: 'jpg', label: 'Image (JPG)' },
  { value: 'jpeg', label: 'Image (JPEG)' },
  { value: 'png', label: 'Image (PNG)' },
  { value: 'gif', label: 'Image (GIF)' },
  { value: 'bmp', label: 'Image (BMP)' },
  { value: 'webp', label: 'Image (WebP)' },
  { value: 'mp4', label: 'Video (MP4)' },
  { value: 'mov', label: 'Video (MOV)' },
  { value: 'avi', label: 'Video (AVI)' },
  { value: 'mp3', label: 'Audio (MP3)' },
  { value: 'wav', label: 'Audio (WAV)' },
  { value: 'zip', label: 'Archive (ZIP)' },
  { value: 'rar', label: 'Archive (RAR)' },
  { value: '7z', label: 'Archive (7Z)' },
  { value: 'txt', label: 'Text' },
  { value: 'dwg', label: 'Drawing (DWG)' },
];

const ColumnVisibilityMenu = ({ isOpen, position, columns, visibleColumns, setVisibleColumns, setIsOpen }) => {
  const ref = useRef(null);
  useClickAway(() => setIsOpen(false), ref);

  if (!isOpen) return null;

  const menuHeight = columns.filter(c => c.key).length * 32 + 48;
  const style = {
    position: 'fixed',
    left: position.x,
    top: Math.min(position.y + 8, window.innerHeight - menuHeight),
    zIndex: FILE_MANAGEMENT_POPUP_Z_INDEX,
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04)',
    border: '1px solid #f0f0f0',
    padding: '8px 4px',
    minWidth: 180,
  };

  const handleToggle = (key) => {
    if (!visibleColumns) {
      // First toggle: hide all except the clicked one
      setVisibleColumns(columns.filter(c => c.key && c.key !== key).map(c => c.key));
    } else if (visibleColumns.includes(key)) {
      setVisibleColumns(visibleColumns.filter(k => k !== key));
    } else {
      setVisibleColumns(columns.filter(c => c.key && (!visibleColumns.includes(c.key) || c.key === key)).map(c => c.key));
    }
  };

  const allVisible = !visibleColumns || columns.every(c => !c.key || visibleColumns.includes(c.key));

  return (
    <div ref={ref} style={style}>
      <div style={{ padding: '4px 8px', fontWeight: 500, fontSize: 13, color: '#666', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
        Show Columns
      </div>
      {columns.filter(c => c.key).map(col => {
        const isVisible = !visibleColumns || visibleColumns.includes(col.key);
        return (
          <div
            key={col.key}
            style={{ padding: '4px 8px', cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 13 }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            onClick={() => handleToggle(col.key)}
          >
            <Checkbox checked={isVisible} style={{ marginRight: 8 }} />
            {col.title}
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 4, padding: '4px 8px 0' }}>
        <Button
          type="link"
          size="small"
          onClick={() => setVisibleColumns(allVisible ? [] : null)}
        >
          {allVisible ? 'Hide All' : 'Show All'}
        </Button>
      </div>
    </div>
  );
};

export const FileManagement = (props) => {

  // const [currentFiles, setCurrentFiles] = useState([]);
  const [searchKeyword, setSearchKeyWord] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const facility = useSelector(state => state.Facility.selected.Id);
  const userId = useSelector(state => state.Auth.userId);
  const canWrite = useAuth('filemanage', AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth('filemanage', AUTH_LEVEL.FULL_ACCESS);
  const isOwner = (record) => record?.createdby === userId;
  const isSearch = debouncedSearch.length > 0;

  const debouncedSetSearch = useCallback(_.debounce((value) => {
    setDebouncedSearch(value);
  }, 300), []);

  const onSearchKeywordChange = (e) => {
    const value = e.target.value;
    setSearchKeyWord(value);
    debouncedSetSearch(value);
  };

  const [currentTreeKey, setCurrentTreeKey] = useState(null);

  const currentFileRef = useRef(null);

  const setCurrentTreeKeyWithClearSearch = (value) => {
    setCurrentTreeKey(value);
    setSearchKeyWord('');
    setDebouncedSearch('');
    setSelectedRowKeys([]);
    setQuickAccess(null);
  }

  const handleQuickAccess = (type) => {
    setQuickAccess(prev => prev === type ? null : type);
    setCurrentTreeKey(null);
    setSearchKeyWord('');
    setDebouncedSearch('');
    setSelectedRowKeys([]);
  }

  const [treeData, setTreeData] = useState([]);
  const [leftWidth, setLeftWidth] = useState(null);
  const wrapRef = useRef(null);
  const leftWidthStyle = leftWidth ? { width: leftWidth } : { width: '20%' };
  const rightWidthStyle = leftWidth ? { width: `calc(100% - ${leftWidth}px)` } : { width: '80%' };

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmTargets, setDeleteConfirmTargets] = useState([]);
  const [deleteConfirmSubmitting, setDeleteConfirmSubmitting] = useState(false);
  const [isFileOpen, setIsFileOpen] = useState(false);
  const [isEditTagOpen, setIsEditTagOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  const [isPreview, setIsPreview] = useState(false)
  const [viewType, setViewType] = useState('card')

  const [currentOpenFile, setCurrentOpenFile] = useState();

  const [isCreatingFolderLoading, setIsCreatingFolderLoading] = useState(false);
  const [isUploadingFileLoading, setIsUploadingFileLoading] = useState(false);
  const [isReLoadLoading, setIsReLoadLoading] = useState(false);
  const [isRenamingLoading, setIsRenameLoading] = useState(false);

  const [newFolderForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [renameForm] = Form.useForm();
  const [tagForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [moveForm] = Form.useForm()
  const [refreshKey, { inc: update }] = useCounter(0)
  const [visibleColumns, setVisibleColumns] = useState(null);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [columnMenuPos, setColumnMenuPos] = useState({ x: 0, y: 0 });
  const [isDragOver, setIsDragOver] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const rootRef = useRef(null);
  const selectionAnchor = useRef(null);
  const originalFilesRef = useRef({
    files: [],
    folders: []
  });

  // Quick Access: Recent & Favorites
  const [quickAccess, setQuickAccess] = useState(null); // null | 'recent' | 'favorites'
  const [favoriteIds, setFavoriteIds] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('fileFavorites') || '[]');
      return raw.map(String);
    } catch { return []; }
  });

  const isFavorited = (id) => favoriteIds.includes(String(id));

  const toggleFavorite = (id) => {
    const key = String(id);
    setFavoriteIds(prev => {
      const next = prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key];
      localStorage.setItem('fileFavorites', JSON.stringify(next));
      message.success(prev.includes(key) ? 'Removed from Favorites' : 'Added to Favorites');
      return next;
    });
  };
  // const clickTimerRef = useRef(null)

  const isFirstLoading = useRef(true)

  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const [previewUrl, isFileViewLoading] = usePreviewUrl(currentOpenFile)
  useEffect(() => {
    handleGetData()
  }, [])

  const onSelect = (_, e) => {
    setCurrentTreeKeyWithClearSearch(e.selectedNodes[0])
  }

  const handleDrag = (e) => {
    const containerRect = wrapRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    setLeftWidth(Math.max(newWidth, 160)); // minimum 160px for tree panel
  };

  const currentFiles = useMemo(() => {
    let allItems;

    if (quickAccess === 'recent') {
      // Recent: all files sorted by modification date descending
      allItems = [...originalFilesRef.current.files]
        .sort((a, b) => new Date(b.modified_date || b.created_date || 0) - new Date(a.modified_date || a.created_date || 0));
    } else if (quickAccess === 'favorites') {
      // Favorites: only pinned items
      allItems = [].concat(
        originalFilesRef.current.folders.filter(f => favoriteIds.includes(String(f.id))),
        originalFilesRef.current.files.filter(f => favoriteIds.includes(String(f.id)))
      );
    } else if (isSearch) {
      allItems = [].concat(originalFilesRef.current.folders, originalFilesRef.current.files);
    } else {
      allItems = currentTreeKey?.nodes ?? [];
    }

    let filtered = isSearch
      ? allItems.filter(item => item.name && item.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
      : allItems;

    // Apply file type filter
    if (filterType) {
      filtered = filtered.filter(item => {
        if (item.isFolder) return false;
        const ext = getFileExtension(item.name || item.title);
        return ext === filterType;
      });
    }

    // Apply category filter
    if (filterCategory) {
      filtered = filtered.filter(item => {
        if (item.isFolder) return false;
        return item.category === filterCategory;
      });
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTreeKey, debouncedSearch, filterType, filterCategory, quickAccess, favoriteIds])

  const resolveDeleteParams = useCallback((record) => {
    const isFolder = !!record.isFolder;
    let id = record.id;
    let record_id = record.record_id;
    let table_name = record.table_name;

    if (props.tableName && props.tableName !== 'All') {
      table_name = table_name || props.tableName;
      if (record_id == null || record_id === '') {
        record_id = props.recordId;
      }
    } else if ((!table_name || record_id == null || record_id === '') && record.parentFolder) {
      let p = record.parentFolder;
      while (p) {
        const ctx = p.context;
        if (ctx && ctx.table_name && ctx.table_name !== 'All') {
          if (!table_name) table_name = ctx.table_name;
          if (record_id == null || record_id === '') record_id = ctx.record_id;
        }
        if (!table_name && p.table_name) table_name = p.table_name;
        if ((record_id == null || record_id === '') && p.record_id != null && p.record_id !== '') {
          record_id = p.record_id;
        }
        if (table_name && (record_id != null && record_id !== '')) break;
        p = p.parentFolder;
      }
    }

    return { id, isFolder, record_id, table_name };
  }, [props.tableName, props.recordId]);

  const buildDeleteQueryParams = ({ record_id, table_name }) => {
    const params = {};
    if (table_name) params.table_name = table_name;
    if (record_id != null && record_id !== '') params.record_id = record_id;
    return params;
  };

  const isSuccessfulDelete = (res) => res && res.status >= 200 && res.status < 300;

  const onSelectChange = useCallback((keys) => {
    const keySet = new Set(keys);
    setSelectedRowKeys(currentFiles.filter((r) => keySet.has(r.key)));
  }, [currentFiles]);

  const rowSelection = useMemo(() => ({
    selectedRowKeys: selectedRowKeys.map(item => item.key).filter(Boolean),
    onChange: onSelectChange,
  }), [selectedRowKeys, onSelectChange]);

  useEffect(() => {
    setSelectedRowKeys((prev) =>
      prev.filter((r) => currentFiles.some((f) => f.key === r.key))
    );
  }, [currentFiles]);

  // Keyboard shortcuts
  useEffect(() => {
    const isModalOpen = isEditOpen || isUploadOpen || isRenameOpen || isEditTagOpen || isMoveToOpen || isDeleteConfirmOpen || newFolderOpen || isFileOpen;
    const handleKeyDown = (e) => {
      if (isModalOpen) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.target.isContentEditable) return;

      const sel = selectedRowKeys;

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'a':
            e.preventDefault();
            setSelectedRowKeys([...currentFiles]);
            break;
          case 'c':
            e.preventDefault();
            if (sel.length === 1 && !sel[0].isFolder) {
              copyLink(sel[0]);
              message.success('Link copied to clipboard');
            }
            break;
        }
      } else {
        switch (e.key) {
          case 'Delete':
          case 'Backspace':
            if (sel.length > 0 && sel.every(item => !item.isSystem) && (canFullAccess || sel.every(item => isOwner(item)))) {
              setDeleteConfirmTargets(sel);
              setIsDeleteConfirmOpen(true);
            }
            break;
          case 'F2':
            e.preventDefault();
            if (sel.length === 1 && !sel[0].isSystem && canWrite) {
              openRenameModal(sel[0]);
            }
            break;
          case 'Escape':
            if (isFileOpen) {
              setIsFileOpen(false);
              break;
            }
            setSelectedRowKeys([]);
            break;
          case 'Enter':
            if (sel.length === 1) {
              const record = sel[0];
              if (record.isFolder) {
                setCurrentTreeKeyWithClearSearch(record);
              } else {
                setCurrentOpenFile(record);
                setIsFileOpen(true);
              }
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            if (currentFiles.length > 0) {
              const lastSelIndex = sel.length > 0 ? currentFiles.findIndex(f => f.key === sel[sel.length - 1].key) : -1;
              const prevIndex = lastSelIndex > 0 ? lastSelIndex - 1 : currentFiles.length - 1;
              if (e.shiftKey && sel.length > 0) {
                setSelectedRowKeys(prev => [...prev, currentFiles[prevIndex]].filter((v, i, a) =>
                  a.findIndex(t => t.key === v.key) === i
                ));
              } else {
                setSelectedRowKeys([currentFiles[prevIndex]]);
              }
            }
            break;
          case 'ArrowDown':
            e.preventDefault();
            if (currentFiles.length > 0) {
              const lastSelIndex = sel.length > 0 ? currentFiles.findIndex(f => f.key === sel[sel.length - 1].key) : -1;
              const nextIndex = lastSelIndex < currentFiles.length - 1 ? lastSelIndex + 1 : 0;
              if (e.shiftKey && sel.length > 0) {
                setSelectedRowKeys(prev => [...prev, currentFiles[nextIndex]].filter((v, i, a) =>
                  a.findIndex(t => t.key === v.key) === i
                ));
              } else {
                setSelectedRowKeys([currentFiles[nextIndex]]);
              }
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedRowKeys, currentFiles, currentTreeKey, isEditOpen, isUploadOpen, isRenameOpen, isEditTagOpen, isMoveToOpen, isDeleteConfirmOpen, newFolderOpen, isFileOpen, canWrite, canFullAccess, userId]);

  // Desktop drag-and-drop file upload
  useEffect(() => {
    const container = wrapRef.current;
    if (!container) return;

    let dragCounter = 0;
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
        setIsDragOver(true);
      }
    };
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter === 0) {
        setIsDragOver(false);
      }
    };
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;
      if (!canWrite) return;

      const fileList = files.map(file => ({
        originFileObj: file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));
      uploadForm.setFieldsValue({ files: fileList });
      setIsUploadOpen(true);
    };

    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('drop', handleDrop);
    return () => {
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('drop', handleDrop);
    };
  }, [canWrite, uploadForm, setIsUploadOpen, wrapRef]);

  const handleCreateFolder = async () => {

    const { name } = await newFolderForm.validateFields().catch(() => ({}));

    if (name) {
      setIsCreatingFolderLoading(true)
      try {

        let parent = currentTreeKey?.id;
        let equipment = props.equipmentId;
        let table_name = props.tableName
        let record_id = props.recordId

        if (currentTreeKey.isSystem) {
          parent = null;
          equipment = currentTreeKey.context.equipmentId;
          table_name = currentTreeKey.context.table_name;
          record_id = currentTreeKey.context.record_id;
        } else if (props.tableName === 'All') {
          table_name = currentTreeKey.table_name
          record_id = currentTreeKey.record_id;
          equipment = currentTreeKey.equipment;
        } else {
          table_name = props.tableName
          record_id = props.recordId
          equipment = props.equipmentId
        }

        const result = await api.createFolder({
          name,
          table_name,
          ...(record_id ? { record_id } : {}),
          parent,
          facility,
          equipment
        });
        if (result.status === 201) {
          globalMessage.success({
            message: 'Folder created successfully',
            description: ''
          });
          // this.handleGetData();
        } else {
          globalMessage.error({
            message: 'Failed to create folder',
            description: result.data.message || ''
          });
        }
      } catch (error) {
        globalMessage.error({
          message: 'Failed to create folder',
          description: error.message || ''
        });
      }
      await refresh();
      setNewFolderOpen(false);
      setIsCreatingFolderLoading(false)
    }
  }

  const handleGetData = async () => {
    setIsReLoadLoading(true)
    // Save current position before refresh
    const savedPosition = quickAccess
      ? { type: 'quickAccess', value: quickAccess }
      : { type: 'folder', id: currentTreeKey?.id };

    let query = 'page=' + 1 + '&page_size=' + 9999 + '&table_name=' + props.tableName + (props.recordId ? ('&record_id=' + props.recordId) : '');
    const [filesResult, foldersResult] = await Promise.all([api.getFiles(query), api.getFolders(query)]);
    jsforceResponse(filesResult, '', false, true);
    jsforceResponse(foldersResult, '', false, true);

    if (filesResult.status === 200 && foldersResult.status === 200) {

      const [resultData, treeKey, originalFiles] = formatFilesToTreeData(_.get(filesResult, 'data.results') ?? [], _.get(foldersResult, 'data.results') ?? [], savedPosition.type === 'folder' ? savedPosition.id : undefined, props)
      setTreeData([resultData])
      rootRef.current = resultData;
      originalFilesRef.current = originalFiles;

      // Restore position
      setSearchKeyWord('');
      setDebouncedSearch('');
      setSelectedRowKeys([]);
      if (savedPosition.type === 'quickAccess') {
        setCurrentTreeKey(null);
        setQuickAccess(savedPosition.value);
      } else {
        setCurrentTreeKey(treeKey || resultData);
        setQuickAccess(null);
      }

      isFirstLoading.current = false;
      update();
      setIsReLoadLoading(false)
    }
  }

  const renderPath = () => {
    const arr = getPathsByCurrentTree(currentTreeKey)
    if (isSearch) {
      return <Breadcrumb className="file-breadcrumb" separator="/">
        <Breadcrumb.Item onClick={() => setCurrentTreeKeyWithClearSearch(rootRef.current)}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>Search Results</Breadcrumb.Item>
      </Breadcrumb>
    }
    return (
      <Breadcrumb className="file-breadcrumb" separator="/">
        <Breadcrumb.Item onClick={() => setCurrentTreeKeyWithClearSearch(rootRef.current)}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => setCurrentTreeKeyWithClearSearch(rootRef.current)}>
          root
        </Breadcrumb.Item>
        {
          arr.map(item => (
            <Breadcrumb.Item
              key={item.key || item.name}
              onClick={() => setCurrentTreeKeyWithClearSearch(item)}
            >
              {item.name}
            </Breadcrumb.Item>
          ))
        }
      </Breadcrumb>
    )
  }

  const handleUpload = async () => {
    const values = await uploadForm.validateFields().catch(() => ({
      isErr: true,
    }));
    if (values.isErr) {
      return
    }
    const { tags, category, description, files } = values;
    // const file = values.file?.[0];
    if (files.length === 0) {
      globalMessage['error']({
        message: 'Please upload a file',
        description: ''
      });
      return
    }

    let record_id, equipment, table_name

    if (props.tableName === 'All') {
      if (currentTreeKey.isSystem) {

        record_id = currentTreeKey.context.record_id
        table_name = currentTreeKey.context.table_name
        equipment = currentTreeKey.context.equipmentId
      } else {
        record_id = currentTreeKey.record_id
        table_name = currentTreeKey.table_name
        equipment = currentTreeKey.equipment
      }
    } else {
      record_id = props.recordId
      table_name = props.tableName
      equipment = props.equipmentId
    }


    const body = {
      table_name,
      record_id,
      facility,
      files,
      category,
      description,
      // tags : JSON.stringify(Array.isArray(tags) ? tags : []),
      tags,
      equipment: props.tableName === 'All' ? currentTreeKey?.context?.equipmentId ?? props.equipment : props.equipmentId
    }
    if (!record_id) {
      delete body.record_id
    }

    if (currentTreeKey?.isSystem) {
      body.folder = null;
    } else if (currentTreeKey?.id) {
      body.folder = currentTreeKey?.id;
    }
    setIsUploadingFileLoading(true)
    let result1 = await api.uploadFile(body);
    jsforceResponse(result1, '', false, true)
    if (result1.status >= 200 && result1.status < 300) {
      await handleGetData()
      setIsUploadOpen(false);
      uploadForm.resetFields();
    }
    await refresh();
    setIsUploadingFileLoading(false)
  }

  const refresh = async () => {
    setSelectedRowKeys([])
    return await handleGetData()
  }

  const reload = async () => {
    setIsReLoadLoading(true)
    await handleGetData()
    setSelectedRowKeys([])
    setIsReLoadLoading(false)
  }

  const deleteMultipleRecords = async (records) => {
    if (!records?.length) return false;
    try {
      const results = await Promise.all(
        records.map((record) => {
          const resolved = resolveDeleteParams(record);
          if (resolved.id == null || resolved.id === '') {
            return Promise.resolve({ status: 400, data: { message: 'Missing id' } });
          }
          if (!resolved.table_name) {
            return Promise.resolve({ status: 400, data: { message: 'Missing table name' } });
          }
          const del = resolved.isFolder ? api.deleteFolder : api.deleteFile;
          return del(resolved.id, buildDeleteQueryParams(resolved));
        })
      );
      const failed = results.find((r) => !isSuccessfulDelete(r));
      if (failed) {
        message.error(_.get(failed, 'data.message') || _.get(failed, 'data.detail') || _.get(failed, 'message') || 'Delete failed');
        return false;
      }
      message.success('Deleted successfully');
      await refresh();
      setSelectedRowKeys([]);
      return true;
    } catch (err) {
      message.error(err.message || 'Delete failed');
      return false;
    }
  };

  /** 所有删除入口先走确认对话框 */
  const requestDeleteConfirmation = (records) => {
    const list = (records || []).filter(Boolean);
    if (!list.length) return;
    const ok = list.every(item => !item.isSystem) && (canFullAccess || list.every(item => isOwner(item)));
    if (!ok) {
      message.error('Cannot delete');
      return;
    }
    setDeleteConfirmTargets(list);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmOk = async () => {
    if (!deleteConfirmTargets.length) return;
    setDeleteConfirmSubmitting(true);
    try {
      const ok = await deleteMultipleRecords(deleteConfirmTargets);
      if (ok) {
        setIsDeleteConfirmOpen(false);
        setDeleteConfirmTargets([]);
      }
    } finally {
      setDeleteConfirmSubmitting(false);
    }
  };

  const handleDeleteConfirmCancel = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteConfirmTargets([]);
  };

  const handleEdit = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) {
      return
    };
    const { name, description, category, tags, file } = await editForm.validateFields().catch((err) => {
      return {}
    });

    if (name) {
      setIsRenameLoading(true);
      const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;

      const { id, record_id, table_name, type } = currentFile;
      const res = await fetch(id, { name, table_name, type, description, category, tags, file, ...(record_id ? { record_id } : {}) }).catch(() => ({}))
      if (res.status === 200) {
        if (selectedRowKeys[0]) {
          selectedRowKeys[0].name = name;
          selectedRowKeys[0].title = name;
        }
        refresh()
        setIsEditOpen(false)
      } else {
        message.error('error')
      }
      setIsRenameLoading(false)
    }
  }

  const handleDownload = async () => {

    for (const { image: downloadUrl, name } of selectedRowKeys) {
      const generate_url = await api.generate_url(getSafeUrl(downloadUrl), false).then(
        response => {
          const url = response.data.url;
          return url
        })

      const link = document.createElement('a');
      link.href = generate_url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link)
      await new Promise(resolve => setTimeout(resolve, 500))
    }
  }
  const openEditModal = (record) => {
    currentFileRef.current = record;

    if (currentFileRef.current.isFolder) {
      editForm.setFieldsValue({
        name: record.name,
        // tags: record.tags.map(item => item.id),
        // description: record.description,
        // category: record.category,
      })
    } else {
      editForm.setFieldsValue({
        name: record.name,
        tags: record.tags_list,
        description: record.description === 'null' ? '' : record.description,
        category: record.category === 'null' ? '' : record.category,
      })
    }

    setIsEditOpen(true)
  }

  const openEditTagModal = (record) => {
    currentFileRef.current = record;
    tagForm.setFieldsValue({
      // name: record.name,
      tags: record.tags_list,
      // description: record.description,
      // category: record.category,
    })
    setIsEditTagOpen(true)
  }
  const openRenameModal = (record) => {
    currentFileRef.current = record;
    renameForm.setFieldsValue({
      name: record.name,
      // tags: record.tags.map(item => item.id),
      // description: record.description,
      // category: record.category,
    })
    setIsRenameOpen(true)
  }

  const handleRename = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) return;

    const { name } = await renameForm.validateFields().catch(() => ({}));
    if (name) {
      setIsRenameLoading(true);
      // const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;

      const { id, record_id, table_name, type, description, category, tags } = currentFile;
      let res;
      if (currentFile.isFolder) {
        res = await api.modifyFolder(id, { name, ...(record_id ? { record_id } : {}), table_name, type, description, category, tags, facility }).catch(() => ({}))

      } else {
        res = await api.modifyFile(id, { name, ...(record_id ? { record_id } : {}), table_name, type, description, category, tags }).catch(() => ({}))
      }
      if (res.status === 200) {
        if (selectedRowKeys[0]) {
          selectedRowKeys[0].name = name;
          selectedRowKeys[0].title = name;
        }
        refresh()
        setIsRenameOpen(false)
      } else {
        message.error('error')
      }
      setIsRenameLoading(false)
    }
  }

  const openMoveToModal = (record) => {
    setIsMoveToOpen(true)
    currentFileRef.current = record;
    moveForm.setFieldsValue({
      folder: record.folder
    })
  }

  const handleMoveTo = async () => {
    const currentFile = currentFileRef.current;
    const { id, type, description, category, tags, name } = currentFile;

    const fromData = await moveForm.validateFields().catch(err => {
      message.error('Please select a folder')
    });
    if (!fromData.folder) {
      return
    }

    const selectedFolder = originalFilesRef.current.folders.find(item => item.id === fromData.folder)
    const folder = fromData.folder
    let record_id, table_name, equipment
    if (selectedFolder.isSystem) {
      record_id = selectedFolder.context.record_id
      table_name = selectedFolder.context.table_name
      equipment = selectedFolder.context.equipmentId
    } else {
      record_id = currentFile.record_id
      table_name = currentFile.table_name
      equipment = currentFile.equipment
    }

    const fetch = selectedFolder.isFolder ? api.modifyFolder : api.modifyFile;

    const body = {
      name,
      record_id,
      table_name,
      equipment,
      type,
      description,
      category,
      tags,
      folder: selectedFolder.isSystem ? null : folder
    }
    if (!body.record_id) {
      delete body.record_id
    }

    const res = await fetch(id, body, true)
    if (res.status === 200) {
      refresh()
      setIsMoveToOpen(false)
    } else {
      message.error('error')
    }
  }

  const handleDropOnFolder = async (draggedData, targetFolder) => {
    const draggedItem = currentFiles.find(f => f.key === draggedData.key || f.id === draggedData.id);
    if (!draggedItem || draggedItem.isFolder || targetFolder.isSystem) return;

    const { id, type, description, category, tags, name } = draggedItem;
    let record_id, table_name, equipment;

    if (targetFolder.isSystem) {
      record_id = targetFolder.context.record_id;
      table_name = targetFolder.context.table_name;
      equipment = targetFolder.context.equipmentId;
    } else {
      record_id = draggedItem.record_id;
      table_name = draggedItem.table_name;
      equipment = draggedItem.equipment;
    }

    const fetch = draggedItem.isFolder ? api.modifyFolder : api.modifyFile;
    const body = {
      name,
      record_id,
      table_name,
      equipment,
      type,
      description,
      category,
      tags,
      folder: targetFolder.isSystem ? null : targetFolder.id,
    };
    if (!body.record_id) delete body.record_id;

    const res = await fetch(id, body, targetFolder.isFolder).catch(() => ({}));
    if (res.status === 200) {
      message.success(`Moved "${name}" to "${targetFolder.name}"`);
      refresh();
    } else {
      message.error('Failed to move file');
    }
  };

  const handleEditTag = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) return;

    const { tags } = await tagForm.validateFields().catch(() => ({}));
    if (currentFile?.name) {
      setIsRenameLoading(true);
      const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;
      const { id, record_id, table_name, type, description, category, name } = currentFile;
      const res = await fetch(id, { name, record_id, table_name, type, description, category, tags }).catch(() => ({}))
      if (res.status === 200) {
        if (selectedRowKeys[0]) {
          selectedRowKeys[0].name = name;
          selectedRowKeys[0].title = name;
        }
        refresh()
        setIsEditTagOpen(false)
      } else {
        message.error('error')
      }
      setIsRenameLoading(false)
    }
  }


  const columns = useMemo(() => [
    {
      width: 26,
      dataIndex: '',
      ellipsis: true,
      render(_, item) {
        return <div className='icon-wrap'><FileIcon isFolder={item.isFolder} title={item.title} /></div>
      }
    },
    {
      title: 'Name',
      dataIndex: 'title',
      width: 300,
      sorter: (a, b) => a.title.localeCompare(b.title),
      defaultSortOrder: 'ascend',
      render: (text, record) => {
        return (
          <div style={{ position: 'relative', width: '100%' }}>
            {record.isSystem ? null : <div className='NameDropdownMenu' style={{ position: 'absolute', right: 0, top: 0 }}>
              <DropdownMenu
                record={record}
                currentFileRef={currentFileRef}
                setIsEditOpen={setIsEditOpen}
                setIsFileOpen={setIsFileOpen}
                setCurrentOpenFile={setCurrentOpenFile}
                setCurrentTreeKeyWithClearSearch={setCurrentTreeKeyWithClearSearch}
                reload={reload}
                openEditModal={openEditModal}
                onRequestDeleteConfirm={(r) => requestDeleteConfirmation([r])}
                openEditTagModal={openEditTagModal}
                openRenameModal={openRenameModal}
                openMoveToModal={openMoveToModal}
                canWrite={canWrite}
                canFullAccess={canFullAccess}
                userId={userId}
                isFavorited={isFavorited(record.id)}
                toggleFavorite={toggleFavorite}
              />
            </div>}
            <span className="name-text">{text}</span>
          </div>
        );
      },
    },
    {
      title: 'Date Modified',
      dataIndex: 'modified_date',
      key: 'modified_date',
      width: 160,
      sorter: (a, b) => new Date(a.modified_date || 0) - new Date(b.modified_date || 0),
      defaultSortOrder: 'descend',
      render: (text, record) => {
        if (record.isFolder || !text) return null;
        return <span style={{ fontSize: 13, color: '#555' }}>{moment(text).format('MM/DD/YYYY  HH:mm')}</span>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      render: (text, record) => {
        if (record.isFolder) return <span style={{ color: '#888', fontSize: 13 }}>File folder</span>;
        const ext = getFileExtension(record.name || record.title || '');
        const typeMap = {
          pdf: 'PDF File', doc: 'Word Document', docx: 'Word Document',
          xls: 'Excel Spreadsheet', xlsx: 'Excel Spreadsheet',
          ppt: 'PowerPoint', pptx: 'PowerPoint',
          jpg: 'JPEG Image', jpeg: 'JPEG Image', png: 'PNG Image',
          gif: 'GIF Image', bmp: 'Bitmap Image', webp: 'WebP Image', svg: 'SVG Image',
          mp4: 'MP4 Video', mov: 'MOV Video', avi: 'AVI Video', wmv: 'WMV Video',
          mp3: 'MP3 Audio', wav: 'WAV Audio',
          zip: 'ZIP Archive', rar: 'RAR Archive', '7z': '7Z Archive',
          txt: 'Text File', json: 'JSON File', html: 'HTML File',
          dwg: 'DWG Drawing',
        };
        return <span style={{ fontSize: 13, color: '#888' }}>{typeMap[ext] || (ext ? ext.toUpperCase() + ' File' : 'File')}</span>;
      },
    },
    {
      title: 'Size',
      key: 'size',
      dataIndex: 'size',
      width: 90,
      sorter: (a, b) => (a.size || 0) - (b.size || 0),
      render: (bytes, row) => row.isFolder ? null : <span style={{ fontSize: 13, color: '#555' }}>{formatFileSize(bytes)}</span>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags_list',
      width: 200,
      render(record = [], row) {
        if (row.isFolder) return null;
        if (Array.isArray(record) && record.length > 0) {
          return <Tooltip
            overlayStyle={{ zIndex: FILE_MANAGEMENT_POPUP_Z_INDEX }}
            title={<div style={{ maxHeight: 600, overflowY: 'auto' }}>{record.map(item => <div key={item}>{item}</div>)}</div>}
          >
            <div style={{ maxWidth: 180, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {record.map((item, i) => <TagPill key={i} name={item} size="sm" />)}
            </div>
          </Tooltip>;
        }
        return null;
      }
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render(record, row) {
        if (row.isFolder) return null;
        return <CategoryTag type={record} />;
      }
    },
    {
      title: 'Modified By',
      key: 'lastModified_name',
      dataIndex: 'lastModified_name',
      width: 130,
      render: (text, record) => {
        if (record.isFolder || !text) return null;
        return <span style={{ fontSize: 13, color: '#555' }}>{text}</span>;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'created_date',
      key: 'created_date',
      width: 160,
      sorter: (a, b) => new Date(a.created_date || 0) - new Date(b.created_date || 0),
      render: (text, record) => {
        if (record.isFolder || !text) return null;
        return <span style={{ fontSize: 13, color: '#888' }}>{moment(text).format('MM/DD/YYYY  HH:mm')}</span>;
      },
    },
    {
      title: 'Created By',
      key: 'createdby_name',
      dataIndex: 'createdby_name',
      width: 130,
      render: (text, record) => {
        if (record.isFolder || !text) return null;
        return <span style={{ fontSize: 13, color: '#555' }}>{text}</span>;
      },
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      width: 200,
      ellipsis: true,
      render(record, row) {
        if (row.isFolder) return null;
        if (record === 'null' || record === 'undefined' || !record) return null;
        return <span style={{ fontSize: 13, color: '#888' }}>{record}</span>;
      }
    },
  ], []);

  const displayedColumns = useMemo(() => {
    if (!visibleColumns) return columns;
    return columns.filter(col => col.key === '' || visibleColumns.includes(col.key));
  }, [columns, visibleColumns]);

  const onHeaderRow = () => ({
    onContextMenu: (e) => {
      e.preventDefault();
      setIsColumnMenuOpen(true);
      setColumnMenuPos({ x: e.clientX, y: e.clientY });
    },
  });

  const onRow = record => {
    return {
      onDoubleClick(e) {
        if (e.ctrlKey) {
          return
        }
        const element = e.target.closest('.ant-checkbox-wrapper')
        if (element) {
          return
        }

        if (record.isFolder) {
          setCurrentTreeKeyWithClearSearch(record)
        } else {
          setCurrentOpenFile(record)
          setIsFileOpen(true)
        }
      },
      onClick(e) {
        const element = e.target.closest('.ant-checkbox-wrapper')
        if (element) {
          return
        }
        if (record.isFolder) {
          setCurrentOpenFile(undefined)
        } else {
          setCurrentOpenFile(record)
        }

        if (e.shiftKey && selectionAnchor.current) {
          const anchorIndex = currentFiles.findIndex(f => f.key === selectionAnchor.current);
          const currentIndex = currentFiles.findIndex(f => f.key === record.key);
          if (anchorIndex === -1 || currentIndex === -1) {
            setSelectedRowKeys([record]);
            selectionAnchor.current = record.key;
            return;
          }
          const [start, end] = [Math.min(anchorIndex, currentIndex), Math.max(anchorIndex, currentIndex)];
          const rangeItems = currentFiles.slice(start, end + 1);
          if (e.ctrlKey || e.metaKey) {
            const existingKeys = new Set(selectedRowKeys.map(k => k.key));
            const newKeys = [...selectedRowKeys];
            for (const item of rangeItems) {
              if (!existingKeys.has(item.key)) {
                newKeys.push(item);
              }
            }
            setSelectedRowKeys(newKeys);
          } else {
            setSelectedRowKeys(rangeItems);
          }
        } else if (e.ctrlKey || e.metaKey) {
          const currentSelectedRow = selectedRowKeys.find(item => item.key === record.key)
          if (currentSelectedRow) {
            setSelectedRowKeys(selectedRowKeys.filter(item => item.key !== record.key))
          } else {
            setSelectedRowKeys([...selectedRowKeys, record])
          }
          selectionAnchor.current = record.key;
        } else {
          setSelectedRowKeys([record])
          selectionAnchor.current = record.key;
        }
      },
      onContextMenu(e) {
        e.preventDefault()

        const currentSelectedRow = selectedRowKeys.find(item => item.key === record.key)
        if (!currentSelectedRow) {
          setSelectedRowKeys([record])
        }

        setIsContextMenuOpen(true)
        setContextMenuPosition({ x: e.clientX, y: e.clientY })
      }
    }
  }

  const size = useSize(document.body);
  const height = size.height - 120;
  const treeSectionRef = useRef(null);
  const treeSectionSize = useSize(treeSectionRef);

  const canCreateFolder = canWrite && (currentTreeKey?.isSystem ? currentTreeKey?.context?.canCreate : true)
  const canUploadFile = canWrite && (currentTreeKey?.isSystem ? currentTreeKey?.context?.canUpload : true)
  const canDeleteSelected = selectedRowKeys.every(item => !item.isSystem) && (canFullAccess || selectedRowKeys.every(item => isOwner(item)))

  const onMenuItemClick = async (menuItem) => {
    setIsContextMenuOpen(false)
    switch (menuItem.key) {
      case 'OpenFolder':
        setCurrentTreeKeyWithClearSearch(selectedRowKeys[0])
        break;
      case 'Open':
        break;
      case 'OpenInBrowser':
        openInBrowser(selectedRowKeys[0])
        break;
      case 'OpenInApp':
        openInApp(selectedRowKeys[0])
        break;
      case 'Edit':
        openEditModal(selectedRowKeys[0])
        break;
      case 'Rename':
        openRenameModal(selectedRowKeys[0])
        break;
      case 'EditTags':
        openEditTagModal(selectedRowKeys[0])
        break;
      case 'Download':
        for (const item of selectedRowKeys) {
          await handleDownload(item)
        }
        break;
      case 'Refresh':
        reload()
        break;
      case 'Delete':
        requestDeleteConfirmation(selectedRowKeys);
        break;
      case 'CopyLink':
        copyLink(selectedRowKeys[0])
        break;
      case 'MoveTo':
        openMoveToModal(selectedRowKeys[0])
        break;
      case 'ToggleFavorite':
        toggleFavorite(selectedRowKeys[0].id)
        break;
    }
  }

  return (
    <>
      <ActionBarWrap>
        <div className="action-left">
          {
            selectedRowKeys?.length === 0 ? <>
              <Button disabled={!currentTreeKey?.parentFolder} onClick={() => setCurrentTreeKeyWithClearSearch(currentTreeKey?.parentFolder)} title="Go up"><VerticalAlignTopOutlined /></Button>

              {
                canCreateFolder && <Button disabled={isSearch} onClick={() => {
                  newFolderForm.setFieldsValue({
                    name: 'New Folder',
                    path: [].concat('root', getPathsByCurrentTree(currentTreeKey).map(item => item.name)).join('/')
                  })
                  setNewFolderOpen(true)
                }}><FolderAddOutlined />Create Folder</Button>
              }

              {
                canUploadFile && <Button disabled={isSearch} onClick={() => setIsUploadOpen(true)}><UploadOutlined />Upload</Button>
              }
              <Button icon={<ReloadOutlined />} loading={isReLoadLoading} disabled={isReLoadLoading} onClick={reload}>Refresh</Button>
            </> : <>
              {selectedRowKeys?.length === 1 ? <>
                {canWrite && !selectedRowKeys[0].isSystem ? <Button icon={<ScissorOutlined />} onClick={() => openMoveToModal(selectedRowKeys[0])}>Move To</Button> : null}
                {canWrite && !selectedRowKeys[0].isSystem ? <Button icon={<HighlightOutlined />} onClick={() => openRenameModal(selectedRowKeys[0])}>Rename</Button> : null}
                {
                  canDeleteSelected && (
                    <Button
                      onClick={() => requestDeleteConfirmation(selectedRowKeys)}
                      icon={<DeleteOutlined />}
                    >
                      Delete
                    </Button>
                  )
                }
                {selectedRowKeys.every(item => item.isFolder === false) && selectedRowKeys.length !== 0 ? <Button icon={<DownloadOutlined />} onClick={handleDownload}>Download</Button> : null}
              </> :
                <>
                  {
                    canDeleteSelected && (
                      <Button
                        onClick={() => requestDeleteConfirmation(selectedRowKeys)}
                        icon={<DeleteOutlined />}
                      >
                        Delete
                      </Button>
                    )
                  }

                  {selectedRowKeys.every(item => item.isFolder === false) && selectedRowKeys.length !== 0 ? <Button icon={<DownloadOutlined />} onClick={handleDownload}>Download</Button> : null}
                </>
              }

            </>
          }
        </div>
        <div className="action-right">
          <Select
            allowClear
            placeholder="File type"
            className="filter-select"
            value={filterType}
            onChange={setFilterType}
            options={fileTypeOptions}
            size="middle"
          />
          <Select
            allowClear
            placeholder="Category"
            className="filter-select"
            value={filterCategory}
            onChange={setFilterCategory}
            options={CategoryOptions}
            size="middle"
          />
          <Input className='search-input' size="middle" value={searchKeyword} onChange={onSearchKeywordChange} allowClear placeholder="Search files..." prefix={<SearchOutlined />} />
        </div>

      </ActionBarWrap>
      <FileManagementWrap ref={wrapRef} tabIndex={0} style={{ backgroundColor: '#fff', height: 'calc(100vh - 160px)', outline: 'none', position: 'relative' }}>
        {isDragOver && (
          <div className="drag-over-overlay">
            <UploadOutlined />
            <p>Drop files here to upload</p>
          </div>
        )}
        <div className='left' style={leftWidthStyle}>
          <div className="quick-access-section">
            <div className="quick-access-title">Quick Access</div>
            <div
              className={`quick-access-item${quickAccess === 'recent' ? ' quick-access-item-active' : ''}`}
              onClick={() => handleQuickAccess('recent')}
            >
              <ClockCircleOutlined />
              <span>Recent</span>
            </div>
            <div
              className={`quick-access-item${quickAccess === 'favorites' ? ' quick-access-item-active' : ''}`}
              onClick={() => handleQuickAccess('favorites')}
            >
              <StarOutlined />
              <span>Favorites</span>
              {favoriteIds.length > 0 && <span className="quick-access-count">{favoriteIds.length}</span>}
            </div>
          </div>
          <div className="tree-section" ref={treeSectionRef}>
            {isFirstLoading.current ? <Spin style={{ width: '100%', marginTop: 200 }} /> : <Tree.DirectoryTree
              loading={isReLoadLoading}
              key={refreshKey}
              defaultExpandAll
              treeData={treeData}
              onSelect={onSelect}
              icon={(props) => <FolderIcon isOpen={props.expanded && props.data?.children?.length > 0} isSystem={props.data?.isSystem} />}
              switcherIcon={null}
              className='left-tree'
              checkStrictly={true}
              height={treeSectionSize?.height || (quickAccess ? height - 90 : height)}
              selectedKeys={currentTreeKey?.key ? [currentTreeKey.key] : []}
            />}
          </div>
        </div>
        <div
          className="resize-handle"
          onMouseDown={(e) => {
            document.addEventListener('mousemove', handleDrag);
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleDrag);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            document.addEventListener('mouseup', handleMouseUp);
          }}
          style={{
            height: 'calc(100vh - 160px)',
          }}
        />
        <div className='right' style={{ ...rightWidthStyle }}>
          <div className='action-wrap'>
            <div>
              {renderPath()}
            </div>
            <div className='search-bar'>
              <Segmented
                className="action-segment"
                value={isPreview}
                onChange={setIsPreview}
                options={[
                  {
                    value: false,
                    icon: <EyeInvisibleOutlined />,
                  },
                  {
                    value: true,
                    icon: <EyeOutlined />,
                  },
                ]}
              />
              <Segmented
                className="action-segment"
                value={viewType}
                onChange={setViewType}
                options={[
                  {
                    value: 'card',
                    icon: <AppstoreOutlined />,
                  },
                  {
                    value: 'list',
                    icon: <BarsOutlined />,
                  },
                ]}
              />
            </div>
          </div>
          {
            debouncedSearch && <Alert
              className="file-search-alert"
              message={`Search results for "${debouncedSearch}" — ${currentFiles.length} match${currentFiles.length !== 1 ? 'es' : ''} found.`}
              type="info"
              showIcon
              closable
              onClose={() => { setSearchKeyWord(''); setDebouncedSearch(''); }}
            />
          }

          <div className='content'>
            <div className='file-list'>
              <ConfigProvider >
                {
                  viewType === 'card' ?
                    <CardTable
                      dataSource={currentFiles}
                      selectedRowKeys={selectedRowKeys}
                      setSelectedRowKeys={setSelectedRowKeys}
                      onRow={onRow}
                      onDropOnFolder={handleDropOnFolder}
                    /> : <Table
                      rowKey="key"
                      loading={isReLoadLoading}
                      rowSelection={rowSelection}
                      dataSource={currentFiles.map(({ children, ...rest }) => rest)}
                      columns={displayedColumns}
                      pagination={false}
                      onRow={onRow}
                      onHeaderRow={onHeaderRow}
                      locale={tablelocale(isSearch)}
                    />
                }

              </ConfigProvider>
            </div>
            {
              /* 与下方 Drawer 互斥：避免同一 hook 驱动两个 FileViewer（双层预览 / 重复 iframe） */
              isPreview && !isFileOpen && (
              <div className='preview-wrap'>
                <Divider type="vertical" style={{ height: '100%' }} />
                <div className='preview'
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 0,
                  }}
                >
                  <Spin spinning={isFileViewLoading}>
                    <FileViewer url={previewUrl} currentOpenFile={currentOpenFile} variant="inline" />
                  </Spin>

                </div>
              </div>
            )}
          </div>
          <div className="status-bar">
            <span className="status-bar-count">{currentFiles.length} item{currentFiles.length !== 1 ? 's' : ''}</span>
            {selectedRowKeys.length > 0 && (
              <span className="status-bar-selected"> | {selectedRowKeys.length} selected</span>
            )}
          </div>
        </div>

        <ResponsiveModal
          title="New Folder"
          open={newFolderOpen}
          onOk={() => {
            handleCreateFolder()
          }}
          destroyOnClose={true}
          okButtonProps={{
            loading: isCreatingFolderLoading,
            disabled: isCreatingFolderLoading,
          }}
          onCancel={() => setNewFolderOpen(false)}
        >
          <Form form={newFolderForm}>
            <Form.Item label="Name" name='name' rules={[
              { required: true, message: 'Please input folder name' }, {
                validator: (_, value) => {
                  if (currentFiles.some(item => item.name === value)) {
                    return Promise.reject('This folder name is already in use.')
                  }
                  return Promise.resolve();
                }
              }]}>
              <Input placeholder="Folder Name" />
            </Form.Item>
            <Form.Item
              label={<span className="create-info-form-label">Path</span>}
              name="path"
            // initialValue={this.getPath()}
            >
              <Input disabled prefix={<FolderFilled style={{ color: '86909C', marginRight: 4 }} />} />
            </Form.Item>
          </Form>
        </ResponsiveModal>
        <ResponsiveModal
          title="Edit File"
          open={isEditOpen}
          onCancel={() => {
            setIsEditOpen(false);
            currentFileRef.current = selectedRowKeys[0]
          }}
          destroyOnClose={true}
          okButtonProps={{
            loading: isRenamingLoading,
            disabled: isRenamingLoading
          }}
          onOk={handleEdit}
        >
          <Form form={editForm} layout='vertical'>
            <Form.Item label="Name" name='name' rules={[{ required: true, message: 'Please input new name' },
            {
              validator(_, value) {
                if (value === currentFileRef.current.name) {
                  return Promise.resolve()
                }
                if (currentFiles.some(item => item.name === value)) {
                  return Promise.reject('This file name is already in use.')
                }
                return Promise.resolve();
              }
            }
            ]}>
              <Input placeholder="New Name" />
            </Form.Item>
            {!selectedRowKeys?.[0]?.isFolder && <>
              <Form.Item label="Tags" name="tags">
                <FormItemTag record={selectedRowKeys[0]} />
              </Form.Item>
              <Form.Item label="category" name="category">
                <Select options={CategoryOptions} />
              </Form.Item>
              <Form.Item
                name="file"
                label={<div>Upload File<span style={{ color: 'red', marginLeft: 10 }}>{editForm.getFieldValue('file') ? 'This operation may overwrite the original file!' : ''}</span></div>}
                valuePropName="file"
                rules={[{
                  validator() {
                    const file = editForm.getFieldValue('file')
                    if (!file) {
                      return Promise.resolve()
                    }
                    const isValidedFile = checkFileType(file)
                    if (!isValidedFile) {
                      return Promise.reject(`The file \`${file.name}\` is not allowed to upload.please check the file extension.`)
                    }
                    return Promise.resolve();
                  }
                }]}
                getValueFromEvent={(e) => {
                  return e.file
                  // if (Array.isArray(e)) {
                  //   return e;
                  // }
                  // if(Array.isArray(e?.fileList)) {
                  //   return e.fileList;
                  // }
                  // return []; // 确保返回 fileList
                }}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  beforeUpload={beforeUpload}
                  onChange={({ file, fileList }) => {
                    editForm.setFieldsValue({ file: fileList[0] });
                    update();
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                </Upload.Dragger>
              </Form.Item>
              <Form.Item label="description" name="description">
                <Input />
              </Form.Item>
            </>
            }
          </Form>
        </ResponsiveModal>

        <ResponsiveModal title="Edit Tag" open={isEditTagOpen} onCancel={() => setIsEditTagOpen(false)} onOk={handleEditTag}
          okButtonProps={{
            loading: isReLoadLoading,
            disabled: isRenamingLoading,
          }}
        >
          <Form form={tagForm} layout='vertical'>
            <Form.Item label="Tags" name="tags">
              <FormItemTag record={currentFileRef.current} />
            </Form.Item>
          </Form>

        </ResponsiveModal>
        <ResponsiveModal title="Rename" open={isRenameOpen} onCancel={() => setIsRenameOpen(false)} onOk={handleRename}
          okButtonProps={{
            loading: isReLoadLoading,
            disabled: isRenamingLoading,
          }}
        >
          <Form form={renameForm} layout='vertical'>
            <Form.Item label="Name" name='name' rules={[{ required: true },
            {
              validator(_, value) {
                if (value === currentFileRef.current.name) {
                  return Promise.resolve()
                }
                if (currentFiles.some(item => item.name === value)) {
                  return Promise.reject('This file name is already in use.')
                }
                return Promise.resolve();
              }
            }
            ]}>
              <Input placeholder="New Name" />
            </Form.Item>
          </Form>
        </ResponsiveModal>

        <ResponsiveModal
          open={isUploadOpen}
          title="Upload a File"
          onCancel={() => {
            setIsUploadOpen(false);
            uploadForm.resetFields();
          }}
          onOk={handleUpload}
          okButtonProps={{
            loading: isUploadingFileLoading,
            disabled: isUploadingFileLoading,
          }}
          destroyOnClose
        >
          <Form
            form={uploadForm}
            layout="vertical"
            // onFinish={handleFinish}
            autoFocusFirstInput
          >
            <Form.Item label="Tags" name="tags">
              <FormItemTag record={currentTreeKey} />
            </Form.Item>
            <Form.Item label="category" name="category">
              <Select options={CategoryOptions} />
            </Form.Item>

            <Form.Item
              name="files"
              label="Upload File"
              valuePropName="fileList"
              getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
              rules={[{
                validator(_, files) {
                  let validName
                  const isValid = currentFiles.some(c => {
                    return files.some(f => {
                      if (c.name === f.name) {
                        validName = c.name
                        return true
                      } else {
                        return false
                      }
                    })
                  })

                  if (isValid) {
                    return Promise.reject(`File name ${validName} is already in use.`)
                  }

                  for (const file of files) {
                    const isValidedFile = checkFileType(file)
                    if (!isValidedFile) {
                      return Promise.reject(`The file \`${file.name}\` is not allowed to upload.please check the file extension.`)
                    }
                  }

                  return Promise.resolve();
                }
              }]}
            >

              <Upload.Dragger
                multiple={true}
                beforeUpload={beforeUpload}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item label="description" name="description">
              <Input />
            </Form.Item>
          </Form>
        </ResponsiveModal>

        <MoveToModal treeData={treeData} open={isMoveToOpen} setOpen={setIsMoveToOpen} moveForm={moveForm} handleMoveTo={handleMoveTo} />
        <Drawer
          className="file-management-preview-drawer"
          title={currentOpenFile?.title || 'view file'}
          open={isFileOpen}
          onClose={() => setIsFileOpen(false)}
          destroyOnClose={true}
          width="min(92vw, 1200px)"
          height="90vh"
          zIndex={1400}
        >
          <FileViewer url={previewUrl} currentOpenFile={currentOpenFile} variant="drawer" />
        </Drawer>
      </FileManagementWrap>
      <Modal
        centered
        open={isDeleteConfirmOpen}
        zIndex={FILE_MANAGEMENT_DELETE_CONFIRM_MODAL_Z_INDEX}
        okText="OK"
        cancelText="Cancel"
        confirmLoading={deleteConfirmSubmitting}
        onCancel={handleDeleteConfirmCancel}
        onOk={handleDeleteConfirmOk}
        width={416}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingRight: 8 }}>
          <ExclamationCircleOutlined style={{ fontSize: 22, color: '#faad14', flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)', lineHeight: 1.5 }}>
            {deleteConfirmTargets.length <= 1
              ? 'Do you want to delete this record?'
              : `Do you want to delete these ${deleteConfirmTargets.length} records?`}
          </span>
        </div>
      </Modal>
      <ContextMenu onMenuItemClick={onMenuItemClick} x={contextMenuPosition.x} y={contextMenuPosition.y} isContextMenuOpen={isContextMenuOpen} setIsContextMenuOpen={setIsContextMenuOpen} selectedRowKeys={selectedRowKeys} canWrite={canWrite} canFullAccess={canFullAccess} userId={userId} isFavorited={selectedRowKeys.length === 1 && isFavorited(selectedRowKeys[0].id)} />
      <ColumnVisibilityMenu
        isOpen={isColumnMenuOpen}
        position={columnMenuPos}
        columns={columns}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        setIsOpen={setIsColumnMenuOpen}
      />
    </>
  )
}
